import React from 'react';
import { Group, Popover, Radio, Stack, Text } from '@mantine/core';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { ExtractDataStep, FieldExtractModeType } from '@/types/automationSteps.ts';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { HiChevronDown } from 'react-icons/hi2';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import classes from './ExtractSettings.module.css';

const MAX_ENTITY_FIELD_BATCH_SIZE = 25;

type props = {
  step: ExtractDataStep;
  type: 'extract' | 'insights';
};

type Fields = {
  field_extract_mode: FieldExtractModeType;
  insight_extract_mode: FieldExtractModeType;
  entity_field_batch_size: number;
  include_citations: boolean;
};

export function ExtractSettings({ step, type }: props) {
  const { fields, onChange } = useUpdateStepFields<Fields>({ step });

  const currentMode = type === 'extract' ? fields.field_extract_mode : fields.insight_extract_mode;
  const entityFieldBatchInputId = React.useId();

  return (
    <Group gap="xl">
      <Group justify="space-between" align="center" gap={'xs'}>
        <Text size="xs" mb={0} fw={400} c={'dimmed'}>
          Include citations?
        </Text>
        <StyledSwitch
          checked={fields.include_citations}
          onChange={(event) => onChange({ include_citations: event.target.checked })}
          size={'xs'}
        />
      </Group>

      {type === 'extract' && (
        <Group gap={6} wrap="nowrap">
          <Text
            component="label"
            htmlFor={entityFieldBatchInputId}
            size="xs"
            mb={0}
            fw={400}
            c={'dimmed'}
          >
            Entities per field call
          </Text>
          <StyledNumberInput
            id={entityFieldBatchInputId}
            value={fields.entity_field_batch_size ?? 1}
            min={1}
            max={MAX_ENTITY_FIELD_BATCH_SIZE}
            step={1}
            clampBehavior="strict"
            allowDecimal={false}
            hideControls={false}
            w={76}
            size="xs"
            onChange={(value) =>
              onChange({ entity_field_batch_size: typeof value === 'number' ? value : 1 })
            }
          />
        </Group>
      )}

      <Popover position="bottom-end" shadow="lg" width={'400px'}>
        <Popover.Target>
          <StyledButton size={'md'} variant={'outline'} rightSection={<HiChevronDown />}>
            {type === 'extract' ? fields.field_extract_mode : fields.insight_extract_mode}{' '}
            Extraction
          </StyledButton>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack mb={'sm'}>
            <Stack gap={0}>
              <StyledInputLabel>Extraction method</StyledInputLabel>

              <Text size={'11px'} c={'dimmed'} style={{ lineHeight: 1.2 }}>
                This setting only affects {type === 'extract' ? 'Extract Fields' : 'Find Insights'}
              </Text>
            </Stack>

            <Radio.Group
              value={currentMode}
              onChange={(v) =>
                type === 'extract'
                  ? onChange({ field_extract_mode: v as FieldExtractModeType })
                  : onChange({ insight_extract_mode: v as FieldExtractModeType })
              }
            >
              <Stack gap={'xs'}>
                <Radio.Card radius="md" value={FieldExtractModeType.Bulk} className={classes.root}>
                  <Group wrap="nowrap" align="center" px={'md'} py={'sm'}>
                    <Stack gap={'4px'}>
                      <Text size={'sm'} fw={500}>
                        Bulk Extraction
                      </Text>
                      <Text size={'xs'} c={'dimmed'} lh={1.2}>
                        Extract all fields at once from your data source. This is ideal if you want
                        faster processing
                      </Text>
                    </Stack>
                    <Radio.Indicator size={'xs'} />
                  </Group>
                </Radio.Card>
                <Radio.Card
                  radius="md"
                  value={FieldExtractModeType.Individual}
                  className={classes.root}
                >
                  <Group wrap="nowrap" align="center" px={'md'} py={'sm'}>
                    <Stack gap={'4px'}>
                      <Text size={'sm'} fw={500}>
                        Individual Extraction
                      </Text>
                      <Text size={'xs'} c={'dimmed'} lh={1.2}>
                        Extract fields one by one from your data source. This is ideal if your
                        fields are more complex
                      </Text>
                    </Stack>
                    <Radio.Indicator size={'xs'} />
                  </Group>
                </Radio.Card>
              </Stack>
            </Radio.Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}
